import { defineComponent as _defineComponent } from 'vue'
import VsButton from '@/components/vision/VsButton.vue'
import VsText from '@/components/vision/VsText.vue'
import AutoCompleteShell from '@/components/vision/next/inputs/AutoCompleteShell.vue'
import PickerListVirtualized from '@/components/vision/next/picker-list/PickerListVirtualized.vue'
import PickerListItem from '@/components/vision/next/picker-list/PickerListItem.vue'
import ChipFilter from '@/components/vision/next/ChipFilter.vue'
import OnTheFlyLocationForm from '@/components/locations/on-the-fly-locations/OnTheFlyLocationForm.vue'
import { useHeadlessLocationPicker } from './HeadlessLocationPicker'
import { computed, nextTick, ref, toRef } from 'vue'
import { HeadlessAutoCompleteValueType } from '../vision/next/inputs/useAutoComplete'
import { useFormField } from '@/components/vision/next/forms/useFormAdapter'
import { fetchLocationById } from '@/api/locations'
import { useQueryClient } from '@tanstack/vue-query'
import { useHubs } from '@/common/useHubs'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'


export default /*#__PURE__*/_defineComponent({
  __name: 'NewLocationPicker',
  props: {
    label: null,
    name: null,
    hint: null,
    placeholder: null,
    disabled: { type: Boolean },
    required: { type: Boolean },
    errorMessages: null,
    multiple: { type: Boolean },
    value: null,
    excludeLocationIds: null,
    showAddLocation: { type: Boolean },
    canShowAllLocations: { type: Boolean }
  },
  emits: ["input"],
  setup(__props: any, { emit }: { emit: ({
  (event: 'input', value: number | number[] | undefined)
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  label: string
  name?: string
  hint?: string
  placeholder?: string
  disabled?: boolean
  required?: boolean
  errorMessages?: string[]
  multiple?: boolean
  value?: number | number[]
  excludeLocationIds?: number[]
  showAddLocation?: boolean
  canShowAllLocations?: boolean
}



const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()



const valueAsRef = toRef(props, 'value')

const autocomplete = ref<InstanceType<typeof AutoCompleteShell>>()
const virtualizedList = ref<InstanceType<typeof PickerListVirtualized>>()

const isNewLocationFormVisible = ref(false)

const { currentHub } = useHubs()

const showAllLocations = ref<boolean>(false)

const { allErrorMessages: remoteErrors, runValidation } = useFormField({
  value: valueAsRef,
  required: props.required,
})

const allErrors = computed(() => [
  ...remoteErrors.value,
  ...(props.errorMessages ?? []),
])

const getScrollPosition = () => {
  return virtualizedList?.value?.getScrollPosition()
}

const scrollToPosition = (pos: number) => {
  return virtualizedList?.value?.scrollToPosition(pos)
}

const onNavigate = () => {
  autocomplete.value?.input?.focus()
}

const onValueChanged = (
  incoming?: HeadlessAutoCompleteValueType | HeadlessAutoCompleteValueType[]
) => {
  if (props.multiple) {
    const items = incoming as HeadlessAutoCompleteValueType[]
    emit(
      'input',
      items.map((i) => i.id as number)
    )
  } else {
    const item = incoming as HeadlessAutoCompleteValueType | undefined
    emit('input', item?.id)
    if (item) {
      autocomplete.value?.close()
    }
  }
}

const isSelected = (item: HeadlessAutoCompleteValueType) => {
  if (props.multiple) {
    return (valueAsRef.value as number[] | undefined)?.includes(
      item.id as number
    )
  }

  return item.id === valueAsRef.value
}

const client = useQueryClient()

const valueResolver = async (id) => {
  const location = await fetchLocationById(client, id)
  return { id: location.id, title: location.location }
}

const onClose = () => {
  nextTick().then(() => runValidation())
}

const onCloseExpand = () => {
  showAllLocations.value = false
  updateSearch('')
}

const onAddNewLocationClick = () => {
  isNewLocationFormVisible.value = true
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isCreatingLocationOnTheFlyBottomSheetVisible: true,
  })
  autocomplete.value?.close()
}

const onShowMoreResultsClick = () => {
  showAllLocations.value = true
}

const closeNewLocationForm = (locId) => {
  isNewLocationFormVisible.value = false
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isCreatingLocationOnTheFlyBottomSheetVisible: false,
  })
  if (locId) {
    emit('input', locId)

    client.invalidateQueries({
      queryKey: ['locations', {}],
    })
  }
}

const {
  items,
  isFetching,
  locationType,
  locationTypes,
  selectLocationType,
  parentName,
  parentId,
  updateSearch,
  continueFetching,
  navigateIntoItem,
  navigateBack,
  selectItem,
  noResultsMessage,
  clear,
  restoreToPreviousState,
} = useHeadlessLocationPicker({
  getScrollPosition,
  scrollToPosition,
  onNavigate,
  onValueChanged,
  multiple: props.multiple ?? false,
  value: valueAsRef,
  valueResolver,
  hubId: currentHub.value?.id,
  excludeLocationIds: props.excludeLocationIds,
  canShowAllLocations: props.canShowAllLocations,
  showAllLocations,
})

return { __sfc: true,props, jobBottomSheetStatesStore, emit, valueAsRef, autocomplete, virtualizedList, isNewLocationFormVisible, currentHub, showAllLocations, remoteErrors, runValidation, allErrors, getScrollPosition, scrollToPosition, onNavigate, onValueChanged, isSelected, client, valueResolver, onClose, onCloseExpand, onAddNewLocationClick, onShowMoreResultsClick, closeNewLocationForm, items, isFetching, locationType, locationTypes, selectLocationType, parentName, parentId, updateSearch, continueFetching, navigateIntoItem, navigateBack, selectItem, noResultsMessage, clear, restoreToPreviousState, VsButton, VsText, AutoCompleteShell, PickerListVirtualized, PickerListItem, ChipFilter, OnTheFlyLocationForm }
}

})